// bodyvol-20261029: share of each candle's volume traded INSIDE its own body
// (between open and close) vs outside it (wicks). 0–100%, one thin line,
// faint 50% guide. Footprint storage levels, proportional overlap at the
// open/close edges. Closed candles cached by startTime+_dataVersion; the
// forming candle is recomputed each frame (cheap: one candle's levels).
import { useEffect, useRef, memo } from 'react'
import { FIXED_OVERLAY_WIDTH } from '../utils/chartUtils'
import { requestFrame, cancelFrame, FRAME_LAYER } from '../utils/frameDriver'
import { subgraphX } from '../utils/subgraphX'
import { forEachStorageLevel } from '../utils/candleHelpers'
import { placeCrosshairs, registerCrosshairEl, CROSSHAIR_PANE_STYLE } from '../utils/crosshair'

const LINE = 'rgba(245,185,66,0.9)'

export function bodyVolPct(c) {
  if (!c || (c.isHistorical && !c._omFootprint)) return null
  const o = c.open_mcap, cl = c.close_mcap
  if (!(o > 0) || !(cl > 0)) return null
  const lo = Math.min(o, cl), hi = Math.max(o, cl)
  let tot = 0, body = 0
  forEachStorageLevel(c, (step, buy, sell, b) => {
    const v = buy + sell
    if (!v) return
    tot += v
    const a = step * b, z = a + b
    const ov = Math.min(z, hi) - Math.max(a, lo)
    if (ov > 0) body += v * Math.min(1, ov / b)
    else if (hi === lo && a <= lo && lo < z) body += 0   // doji: zero-height body
  })
  // wickrel-20261029: { w: wick share 0–1, t: total volume }
  return tot > 0 ? { w: 1 - body / tot, t: tot } : null
}

function BodyVolSubgraph({ candles, frameRef, containerWidthRef, axisWidth, height = 70, mouseXRef, onCursorXChange }) {
  // wickxh-20261029: crosshair + y readout, same pattern as CumulativeDeltaSubgraph
  const plotAreaRef = useRef(null), yAxisRef = useRef(null), yMagRef = useRef(null), xhairRef = useRef(null)
  const hiRef = useRef(0)
  useEffect(() => registerCrosshairEl(mouseXRef, xhairRef.current), [mouseXRef])
  const showYMag = (y, H) => {
    const el = yMagRef.current, hi = hiRef.current
    if (!el || !hi) return
    const v = hi - ((y - 6) / Math.max(1, H - 12)) * hi
    el.style.top = `${y}px`; el.textContent = v.toFixed(2); el.style.display = ''
  }
  const hideYMag = () => { if (yMagRef.current) yMagRef.current.style.display = 'none' }
  const onPlotPointerMove = (e) => {
    const rect = plotAreaRef.current?.getBoundingClientRect()
    if (!rect) return
    const x = e.clientX - rect.left
    if (mouseXRef) mouseXRef.current = x
    if (onCursorXChange) onCursorXChange(x)
    else if (mouseXRef) placeCrosshairs(mouseXRef, x)
    showYMag(e.clientY - rect.top, rect.height)
  }
  const onPlotPointerLeave = () => {
    if (mouseXRef) mouseXRef.current = -1
    if (onCursorXChange) onCursorXChange(-1)
    else if (mouseXRef) placeCrosshairs(mouseXRef, -1)
    hideYMag()
  }
  const onAxisPointerMove = (e) => {
    const rect = yAxisRef.current?.getBoundingClientRect()
    if (rect) showYMag(e.clientY - rect.top, rect.height)
  }
  const plotRef = useRef(null), axisRef = useRef(null)
  const candlesRef = useRef(candles); candlesRef.current = candles
  const cacheRef = useRef(new Map())
  useEffect(() => {
    let cancelled = false, raf = 0
    const draw = () => {
      if (cancelled) return
      raf = requestFrame(draw, FRAME_LAYER)
      const pc = plotRef.current, ac = axisRef.current
      if (!pc || !ac) return
      const frame = frameRef?.current
      const all = (frame && frame.candles) || candlesRef.current
      const n = all?.length || 0
      const dpr = window.devicePixelRatio || 1, H = height
      const W = Math.max(50, (containerWidthRef.current || 0) - FIXED_OVERLAY_WIDTH)
      const AW = axisWidth || FIXED_OVERLAY_WIDTH
      for (const [cv, w] of [[pc, W], [ac, AW]]) {
        if (cv.width !== w * dpr || cv.height !== H * dpr) { cv.width = w * dpr; cv.height = H * dpr }
        cv.style.width = `${w}px`; cv.style.height = `${H}px`
      }
      const p = pc.getContext('2d'), a = ac.getContext('2d')
      p.setTransform(dpr, 0, 0, dpr, 0, 0); a.setTransform(dpr, 0, 0, dpr, 0, 0)
      p.clearRect(0, 0, W, H); a.clearRect(0, 0, AW, H)
      p.fillStyle = 'rgba(15, 17, 22, 0.35)'; p.fillRect(0, 0, W, H)
      const PAD = 6
      const g = n ? subgraphX(frame, 1) : null
      if (!g) return
      const cache = cacheRef.current
      if (cache.size > 5000) cache.clear()
      // per-candle {w, t}: closed cached by startTime+_dataVersion+open/close
      const stat = (i) => {
        const c = all[i]; if (!c) return null
        if (i === n - 1) return bodyVolPct(c)
        const k = c._dataVersion | 0, e = cache.get(c.startTime)
        if (e && e.k === k && e.o === c.open_mcap && e.c === c.close_mcap) return e.v
        const v = bodyVolPct(c)
        cache.set(c.startTime, { k, o: c.open_mcap, c: c.close_mcap, v })
        return v
      }
      const end = Math.min(g.end, n - 1)
      const from = Math.max(0, g.start - 10)
      const st = []
      for (let i = from; i <= end; i++) st.push(stat(i))
      const vals = []
      let maxV = 0
      for (let i = g.start; i <= end; i++) {
        const s0 = st[i - from]
        let v = null
        if (s0) {
          const prev = []
          for (let j = Math.max(from, i - 10); j < i; j++) { const q = st[j - from]; if (q) prev.push(q.t) }
          if (prev.length) {
            prev.sort((x, y) => x - y)
            const m = prev.length, med = m % 2 ? prev[(m - 1) / 2] : (prev[m / 2 - 1] + prev[m / 2]) / 2
            if (med > 0) v = s0.w * (s0.t / med)
          }
        }
        vals.push(v)
        if (v != null && v > maxV) maxV = v
      }
      const hi = Math.max(maxV, 1) * 1.1
      hiRef.current = hi
      const yFor = v => PAD + (1 - v / hi) * (H - 2 * PAD)
      // faint guide at 1.0 (= normal volume, all in the wicks)
      p.strokeStyle = 'rgba(160,170,190,0.25)'; p.lineWidth = 1; p.setLineDash([4, 3])
      p.beginPath(); p.moveTo(0, yFor(1)); p.lineTo(W, yFor(1)); p.stroke(); p.setLineDash([])
      a.font = '400 12px "Nova Square", system-ui, sans-serif'
      a.textBaseline = 'middle'; a.textAlign = 'center'; a.fillStyle = '#646464'
      a.fillText(hi.toFixed(1), AW / 2, Math.max(8, yFor(hi)))
      if (Math.abs(yFor(1) - yFor(hi)) > 14) a.fillText('1.0', AW / 2, yFor(1))
      a.fillText('0', AW / 2, Math.min(H - 8, yFor(0)))
      let last = null
      p.strokeStyle = LINE; p.lineWidth = 1.25; p.beginPath()
      let pen = false
      for (let i = g.start; i <= end; i++) {
        const v = vals[i - g.start]
        if (v == null) { pen = false; continue }
        const x = g.xLeft(i) + g.cw / 2, y = yFor(v)
        if (pen) p.lineTo(x, y); else { p.moveTo(x, y); pen = true }
        last = v
      }
      p.stroke()
      if (last != null) {
        const y = Math.max(8, Math.min(H - 8, yFor(last)))
        a.fillStyle = LINE; a.fillRect(0, y - 8, AW, 16)
        a.fillStyle = '#0a0a0a'; a.font = '600 12px "Nova Square", system-ui, sans-serif'
        a.fillText(last.toFixed(2), AW / 2, y)
      }
    }
    raf = requestFrame(draw, FRAME_LAYER)
    return () => { cancelled = true; if (raf) cancelFrame(raf) }
  }, [height, axisWidth, frameRef, containerWidthRef])
  return (
    <div className="cvd-subgraph" title="Wick Volume (rel): wick share × volume / median of prior 10"
      style={{ height: `${height}px`, display: 'flex', flexDirection: 'row' }}>
      <div ref={plotAreaRef} style={{ flex: 1, position: 'relative', overflow: 'hidden', cursor: 'crosshair', borderTop: '1px solid #252525' }}
        onPointerMove={onPlotPointerMove} onPointerLeave={onPlotPointerLeave}>
        <canvas ref={plotRef} style={{ display: 'block' }} />
        <div ref={xhairRef} className="chart-crosshair" style={CROSSHAIR_PANE_STYLE} />
      </div>
      <div ref={yAxisRef} style={{ width: `${FIXED_OVERLAY_WIDTH}px`, position: 'relative', flexShrink: 0, borderTop: '1px solid #252525' }}
        onPointerMove={onAxisPointerMove} onPointerLeave={hideYMag}>
        <canvas ref={axisRef} style={{ display: 'block' }} />
        <div ref={yMagRef} className="price-label" style={{ display: 'none', left: 0, right: 0, transform: 'translateY(-50%)' }} />
      </div>
    </div>
  )
}
export default memo(BodyVolSubgraph)
